"use client";

import type { RefObject } from "react";

import { Popover } from "@/components/ui/atoms/popover";
import { TempoControl } from "@/components/ui/molecules/tempo-control";
import { MetronomeSection } from "@/components/ui/molecules/metronome-section";
import type { DivisionId, Signature } from "@/lib/studio/studio";

type TempoMetronomePopoverProps = {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;

  // Tempo
  bpm: number;
  onBpmChange: (bpm: number) => void;
  /** Reference (intrinsic) BPM of the loaded piece, drives the % ratio display. */
  originalBpm?: number;

  // Métronome
  metronomeEnabled: boolean;
  onMetronomeToggle: (enabled: boolean) => void;
  metronomeVolume: number; // 0–100
  onMetronomeVolumeChange: (v: number) => void;

  signature: Signature;
  onSignatureChange: (s: Signature) => void;
  /** True when the loaded piece dictates the signature, picker becomes read-only. */
  signatureLocked: boolean;

  division: DivisionId;
  onDivisionChange: (id: DivisionId) => void;

  accent: boolean;
  onAccentChange: (accent: boolean) => void;
};

const SECTION_LABEL_CLASS =
  "text-[11px] uppercase tracking-wider text-text-muted font-medium";

function formatRatio(bpm: number, originalBpm: number): string {
  const ratio = Math.round((bpm / originalBpm) * 100);

  return `${ratio}%`;
}

export function TempoMetronomePopover({
  anchorRef,
  open,
  onClose,
  bpm,
  onBpmChange,
  originalBpm,
  metronomeEnabled,
  onMetronomeToggle,
  metronomeVolume,
  onMetronomeVolumeChange,
  signature,
  onSignatureChange,
  signatureLocked,
  division,
  onDivisionChange,
  accent,
  onAccentChange,
}: TempoMetronomePopoverProps) {
  const showRatio =
    typeof originalBpm === "number" && originalBpm > 0 && originalBpm !== bpm;

  return (
    <Popover
      anchorRef={anchorRef}
      open={open}
      onClose={onClose}
      placement="bottom-start"
      /* Largeur bornée à l'écran, et hauteur bornée au viewport : en paysage
         téléphone il ne reste qu'environ 280 px sous la toolbar, moins que le
         panneau complet. Sur un écran normal aucune des deux bornes ne mord,
         donc rien ne défile. */
      className="w-[min(340px,calc(100vw-2rem))] max-h-[calc(100dvh-8rem)] overflow-y-auto p-4"
    >
      <div className="flex flex-col gap-4">
        {/* ── Tempo ── */}
        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className={SECTION_LABEL_CLASS}>Tempo</span>
            {showRatio && originalBpm ? (
              <span className="text-[10px] tabular-nums text-text-muted/70 italic">
                {formatRatio(bpm, originalBpm)} · orig. {originalBpm}
              </span>
            ) : null}
          </div>

          <TempoControl value={bpm} onChange={onBpmChange} />
        </section>

        {/* Divider, full-bleed, mimics the SPEC's edge-to-edge separator. */}
        <div className="-mx-4 h-px bg-border/60" />

        {/* ── Métronome ── */}
        <MetronomeSection
          enabled={metronomeEnabled}
          onToggle={onMetronomeToggle}
          signature={signature}
          onSignatureChange={onSignatureChange}
          signatureLocked={signatureLocked}
          division={division}
          onDivisionChange={onDivisionChange}
          accent={accent}
          onAccentChange={onAccentChange}
          volume={metronomeVolume}
          onVolumeChange={onMetronomeVolumeChange}
        />
      </div>
    </Popover>
  );
}
